<template>
  <div class="page-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">出库单</h1>
      <div class="header-right">
        <el-button
          text
          class="print-btn"
          @click="handleJumpToPrint"
          aria-label="打印"
        >
          <el-icon><Printer /></el-icon>
          <span>打印</span>
        </el-button>
      </div>
    </header>

    <main class="content">
      <div class="search-panel">
        <el-form
          class="filter-form"
          label-width="80px"
          @submit.prevent="handleSearch"
        >
          <el-form-item label="状态">
            <el-select
              v-model="searchStatus"
              style="width: 100%"
              @change="handleSearch"
            >
              <el-option :value="0" label="待备料" />
              <el-option :value="1" label="待出库" />
              <el-option :value="2" label="待确认" />
              <el-option :value="3" label="已确认" />
              <el-option :value="4" label="已取消" />
            </el-select>
          </el-form-item>

          <el-form-item label="出库单号">
            <el-input
              v-model="searchWareOutNo"
              clearable
              placeholder="请输入出库单号"
              style="width: 100%"
              @keyup.enter="handleSearch"
            />
          </el-form-item>

          <el-form-item label="送货单号">
            <el-input
              v-model="searchSendNo"
              clearable
              placeholder="请输入送货单号"
              style="width: 100%"
              @keyup.enter="handleSearch"
            />
          </el-form-item>

          <el-form-item class="form-item-actions" label-width="0">
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>

          <el-form-item label="供应商" class="form-item-supplier">
            <el-autocomplete
              v-model="supplierKeyword"
              placeholder="请输入供应商名称"
              :fetch-suggestions="querySupplier"
              clearable
              style="width: 100%"
              @select="selectSupplier"
              @clear="clearSupplier"
            />
          </el-form-item>
        </el-form>
      </div>

      <div v-loading="loading" class="table-panel">
        <div class="table-action-bar">
          <div class="selected-info">
            <template v-if="searchStatus === 0">
              已选择
              <span class="selected-count">{{ selectedIds.length }}</span> 项
            </template>
            <span v-else class="selected-hint">出库单列表</span>
          </div>
          <div class="action-buttons">
            <el-button
              v-if="searchStatus === 0"
              type="primary"
              :disabled="selectedIds.length === 0 || batchLoading"
              :loading="batchLoading"
              @click="handleBatchWareOut"
            >
              {{
                batchLoading ? "出库中..." : `批量出库(${selectedIds.length})`
              }}
            </el-button>
          </div>
        </div>

        <el-table
          ref="tableRef"
          class="data-table"
          :data="list"
          :row-key="(row) => row.id"
          :row-class-name="getRowClassName"
          height="calc(100vh - 260px)"
          border
          stripe
          highlight-current-row
          @row-click="handleRowClick"
          @selection-change="handleSelectionChange"
        >
          <el-table-column
            v-if="searchStatus === 0"
            type="selection"
            :selectable="isRowSelectable"
            fixed="left"
            width="48"
          />
          <el-table-column label="序号" type="index" width="56" fixed="left" />
          <el-table-column
            prop="wareOutNo"
            label="出库单号"
            min-width="180"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              <div class="ware-out-no-wrap">
                <span>{{ row.wareOutNo || "-" }}</span>
                <el-tag
                  v-if="row.isFsck === 1"
                  type="danger"
                  size="small"
                  effect="dark"
                >
                  负数出库
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="sendNo"
            label="送货单号"
            min-width="140"
            show-overflow-tooltip
          />
          <el-table-column
            prop="supplierName"
            label="供应商"
            min-width="140"
            show-overflow-tooltip
          />
          <el-table-column
            prop="factoryName"
            label="工厂"
            min-width="120"
            show-overflow-tooltip
          />
          <el-table-column
            prop="subWareName"
            label="子库"
            min-width="100"
            show-overflow-tooltip
          />
          <el-table-column prop="name" label="创建人" width="90" />
          <el-table-column
            prop="wareOutStatus"
            label="状态"
            width="100"
            align="center"
          >
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.wareOutStatus)" size="small">
                {{ getStatusText(row.wareOutStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="wareOutTime"
            label="出库时间"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column
            prop="failDesc"
            label="出库失败原因"
            min-width="150"
            show-overflow-tooltip
          >
            <template #default="{ row }">
              <span v-if="row.failDesc" class="fail-desc">{{
                row.failDesc
              }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            width="140"
            fixed="right"
            align="center"
          >
            <template #default="{ row }">
              <el-button
                v-if="searchStatus === 0"
                size="small"
                type="primary"
                link
                :disabled="batchLoading"
                @click.stop="handleSingleOut(row)"
              >
                出库
              </el-button>
              <el-button
                size="small"
                type="primary"
                link
                :disabled="batchLoading"
                @click.stop="handleDetail(row.id)"
              >
                详情
              </el-button>
            </template>
          </el-table-column>
          <template #empty>
            <el-empty description="暂无数据" :image-size="80" />
          </template>
        </el-table>

        <div class="pagination">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="current"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="size"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            background
          />
        </div>
      </div>
    </main>

    <el-dialog
      v-model="confirmVisible"
      :title="confirmTitle"
      width="400px"
      :close-on-click-modal="false"
    >
      <div class="confirm-text">{{ confirmMessage }}</div>
      <template #footer>
        <el-button @click="confirmVisible = false" :disabled="batchLoading"
          >取消</el-button
        >
        <el-button
          type="primary"
          @click="confirmAction"
          :disabled="batchLoading"
        >
          {{ batchLoading ? "出库中..." : "确认出库" }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="successModalVisible"
      title="出库成功"
      width="400px"
      :close-on-click-modal="false"
    >
      <div class="confirm-text">请稍后查看是否出库成功</div>
      <template #footer>
        <el-button type="primary" @click="closeSuccessModal">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="detailVisible"
      title="出库单详情"
      width="90%"
      :max-width="1200"
      :close-on-click-modal="false"
    >
      <div v-if="detailLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">加载中...</p>
      </div>
      <div v-else-if="detailData" class="detail-content">
        <div class="detail-info-bar">
          <span class="info-item">
            <span class="info-label">出库单号：</span>
            <span class="info-value">{{ detailData.wareOutNo || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">供应商：</span>
            <span class="info-value">{{ detailData.supplierName || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">状态：</span>
            <span class="info-value">
              <el-tag :type="getStatusTagType(detailData.wareOutStatus)">
                {{ getStatusText(detailData.wareOutStatus) }}
              </el-tag>
            </span>
          </span>
          <span class="info-item">
            <span class="info-label">创建人：</span>
            <span class="info-value">{{ detailData.name || "-" }}</span>
          </span>
          <span class="info-item">
            <span class="info-label">创建时间：</span>
            <span class="info-value">{{ detailData.createTime || "-" }}</span>
          </span>
        </div>

        <div
          v-if="detailData.outDetailList && detailData.outDetailList.length > 0"
          class="detail-section"
        >
          <h3 class="detail-section-title">物料明细</h3>
          <el-table :data="detailData.outDetailList" border size="small">
            <el-table-column prop="materialNo" label="物料编码" />
            <el-table-column prop="materialDesc" label="物料描述" />
            <el-table-column prop="demandNum" label="需求数量" />
            <el-table-column prop="wareOutNum" label="出库数量" />
            <el-table-column prop="prepareNum" label="备货数量" />
            <el-table-column prop="wareKeeperName" label="仓管员" />
          </el-table>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" @click="detailVisible = false"
          >关闭</el-button
        >
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Printer } from "@element-plus/icons-vue";
import {
  getWareOutOrderPage,
  batchWareOut,
  getWareOutOrderDetail,
} from "@/api/wareOut";
import { getSuppliers } from "@/api/stockCheck";
import toast from "@/utils/toast";

const router = useRouter();
const route = useRoute();

const tableRef = ref(null);
const searchStatus = ref(0);
const searchWareOutNo = ref("");
const searchSendNo = ref("");
const supplierKeyword = ref("");
const supplierList = ref([]);
const selectedSupplier = ref(null);
const supplierTimer = ref(null);
const loading = ref(false);
const list = ref([]);
const total = ref(0);
const current = ref(1);
const size = ref(20);
const selectedIds = ref([]);
const batchLoading = ref(false);
const confirmVisible = ref(false);
const confirmTitle = ref("确认出库");
const confirmMessage = ref("");
const successModalVisible = ref(false);
const confirmAction = ref(null);

const detailVisible = ref(false);
const detailLoading = ref(false);
const detailData = ref(null);

const statusMap = {
  0: { text: "待备料", tagType: "warning" },
  1: { text: "待出库", tagType: "info" },
  2: { text: "待确认", tagType: "danger" },
  3: { text: "已确认", tagType: "success" },
  4: { text: "已取消", tagType: "" },
};

const getStatusText = (status) => {
  return statusMap[status]?.text || "未知";
};

const getStatusTagType = (status) => {
  return statusMap[status]?.tagType || "";
};

const getRowClassName = ({ row }) => {
  if (searchStatus.value === 0 && row.wareOutStatus === 0) {
    return "selectable";
  }
  return "";
};

const isRowSelectable = (row) => {
  return searchStatus.value === 0 && row.wareOutStatus === 0;
};

const handleRowClick = (row) => {
  if (searchStatus.value === 0 && row.wareOutStatus === 0 && tableRef.value) {
    tableRef.value.toggleRowSelection(row);
  }
};

const handleSelectionChange = (selection) => {
  selectedIds.value = selection.map((row) => row.id);
};

const handleBatchWareOut = () => {
  if (selectedIds.value.length === 0) return;
  confirmTitle.value = "批量出库";
  confirmMessage.value = `确定要出库选中的 ${selectedIds.value.length} 条单据吗？`;
  confirmAction.value = confirmBatchWareOut;
  confirmVisible.value = true;
};

const handleSingleOut = (item) => {
  confirmTitle.value = "出库";
  confirmMessage.value = `确定要出库单据 ${item.wareOutNo} 吗？`;
  confirmAction.value = () => confirmSingleOut(item.id);
  confirmVisible.value = true;
};

const confirmBatchWareOut = async () => {
  batchLoading.value = true;
  const ids = selectedIds.value.join(",");
  selectedIds.value = [];
  confirmVisible.value = false;

  batchWareOut({ ids }).catch((e) => {
    console.error("批量出库异步请求失败:", e);
  });

  setTimeout(() => {
    successModalVisible.value = true;
    batchLoading.value = false;
  }, 800);
};

const closeSuccessModal = () => {
  successModalVisible.value = false;
  fetchList(true);
};

const handleJumpToPrint = () => {
  router.push({ name: "WareOutPrintList" });
};

const confirmSingleOut = async (id) => {
  batchLoading.value = true;
  try {
    const res = await batchWareOut({ ids: id });
    if (res?.success || res?.code === 200) {
      toast.success("出库提交成功");
      confirmVisible.value = false;
      fetchList(true);
    } else {
      toast.error(res?.message || "出库失败");
    }
  } catch (e) {
    console.error("出库失败:", e);
    toast.error("出库失败");
  } finally {
    batchLoading.value = false;
  }
};

const fetchList = async (isRefresh = false) => {
  if (isRefresh) {
    current.value = 1;
    selectedIds.value = [];
  }

  loading.value = true;

  try {
    const params = {
      wareOutStatus: searchStatus.value,
      current: current.value,
      size: size.value,
    };

    if (searchWareOutNo.value.trim()) {
      params.wareOutNo = searchWareOutNo.value.trim();
    }

    if (searchSendNo.value.trim()) {
      params.sendNo = searchSendNo.value.trim();
    }

    if (selectedSupplier.value) {
      params.supplierId = selectedSupplier.value.id;
    }

    const res = await getWareOutOrderPage(params);
    const records = res?.data?.records || [];
    const totalCount = res?.data?.total || 0;

    total.value = totalCount;
    list.value = records;

    if (records.length === 0 && current.value > 1) {
      current.value = Math.max(1, Math.ceil(totalCount / size.value));
      if (current.value >= 1) {
        await fetchList(false);
      }
    }
  } catch (e) {
    console.error("获取出库单列表失败:", e);
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  fetchList(true);
};

const handleReset = () => {
  searchStatus.value = 0;
  searchWareOutNo.value = "";
  searchSendNo.value = "";
  supplierKeyword.value = "";
  selectedSupplier.value = null;
  supplierList.value = [];
  fetchList(true);
};

const querySupplier = (queryString, callback) => {
  if (supplierTimer.value) window.clearTimeout(supplierTimer.value);
  supplierTimer.value = window.setTimeout(async () => {
    const keyword = queryString.trim();
    if (keyword.length < 2) {
      callback([]);
      return;
    }
    try {
      const res = await getSuppliers({ keyword, size: 10 });
      const data = (res?.data?.records || []).map((item) => ({
        value: item.supplierName,
        ...item,
      }));
      callback(data);
    } catch (e) {
      console.error("搜索供应商失败:", e);
      callback([]);
    }
  }, 500);
};

const selectSupplier = (item) => {
  selectedSupplier.value = item;
};

const clearSupplier = () => {
  selectedSupplier.value = null;
};

const handleSizeChange = (val) => {
  size.value = val;
  fetchList(true);
};

const handleCurrentChange = (val) => {
  current.value = val;
  fetchList(false);
};

const handleDetail = async (id) => {
  detailLoading.value = true;
  detailVisible.value = true;
  try {
    const res = await getWareOutOrderDetail(id);
    if (res?.success || res?.code === 200) {
      detailData.value = res?.data || {};
    } else {
      toast.error(res?.message || "获取详情失败");
    }
  } catch (e) {
    console.error("获取详情失败:", e);
    toast.error("获取详情失败");
  } finally {
    detailLoading.value = false;
  }
};

const handleBack = () => {
  if (route.query?.back) {
    router.push(route.query.back);
  } else {
    router.back();
  }
};

onMounted(() => {
  fetchList(true);
});
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: #f0f2f5;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #23b1c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  cursor: pointer;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.print-btn {
  color: #fff;
  font-size: 14px;
}

.print-btn :deep(.el-icon) {
  margin-right: 4px;
}

.icon-back {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.title {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
}

.header-right {
  min-width: 80px;
  display: flex;
  justify-content: flex-end;
}

.content {
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;
}

.search-panel {
  background: #fff;
  padding: 16px 16px 4px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 12px;
  width: 100%;
}

.filter-form :deep(.el-form-item) {
  margin-bottom: 12px;
  margin-right: 0;
}

.filter-form :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}

.form-item-actions {
  grid-column: 4 / 5;
  align-self: end;
}

.form-item-actions :deep(.el-form-item__content) {
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
  margin-left: 0 !important;
}

.form-item-supplier {
  grid-column: 1 / 2;
}

.table-panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 12px 16px 8px;
}

.table-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  min-height: 32px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.selected-info {
  font-size: 14px;
  color: #606266;
}

.selected-count {
  color: #409eff;
  font-weight: 600;
  margin: 0 4px;
}

.selected-hint {
  color: #909399;
}

.data-table {
  width: 100%;
}

.data-table :deep(.el-table__row.selectable) {
  cursor: pointer;
}

.fail-desc {
  color: #f56c6c;
}

.ware-out-no-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 0 4px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top: 3px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #909399;
  font-size: 14px;
  margin-top: 12px;
}

.detail-content {
  overflow-y: auto;
}

.detail-info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-label {
  font-size: 0.875rem;
  color: #606266;
  margin-right: 0.5rem;
}

.info-value {
  font-size: 0.875rem;
  color: #303133;
}

.detail-section {
  margin-top: 1.5rem;
}

.detail-section-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #303133;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #4db6ac;
}

.confirm-text {
  font-size: 0.9375rem;
  color: #606266;
  line-height: 1.5;
}

@media (max-width: 1200px) {
  .filter-form {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .form-item-actions {
    grid-column: 3 / 4;
  }

  .form-item-supplier {
    grid-column: 1 / 2;
  }
}

@media (max-width: 900px) {
  .filter-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .form-item-actions {
    grid-column: 2 / 3;
  }

  .form-item-supplier {
    grid-column: 1 / 2;
  }
}
</style>
